import React, { useRef, useEffect } from 'react';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

interface ReCaptchaWidgetProps {
  onVerify: (token: string) => void;
  resetKey?: number;
}

const ReCaptchaWidget: React.FC<ReCaptchaWidgetProps> = ({ onVerify, resetKey }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onVerifyRef = useRef(onVerify);
  const renderedRef = useRef(false);

  onVerifyRef.current = onVerify;

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      onVerifyRef.current('dev-bypass-token');
      return;
    }

    const tryRender = () => {
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha?.render || !containerRef.current || renderedRef.current) return;
      renderedRef.current = true;
      widgetIdRef.current = grecaptcha.render(containerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (token: string) => onVerifyRef.current(token),
      });
    };

    if ((window as any).grecaptcha?.render) {
      tryRender();
      return;
    }

    if (document.getElementById('recaptcha-script')) {
      const check = setInterval(() => {
        if ((window as any).grecaptcha?.render) {
          tryRender();
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }

    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = tryRender;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!resetKey) return;
    if (!RECAPTCHA_SITE_KEY) {
      onVerifyRef.current('dev-bypass-token');
      return;
    }
    if (widgetIdRef.current !== null && (window as any).grecaptcha) {
      try { (window as any).grecaptcha.reset(widgetIdRef.current); } catch {}
    }
  }, [resetKey]);

  if (!RECAPTCHA_SITE_KEY) return null;

  return <div ref={containerRef} />;
};

export default ReCaptchaWidget;
